import { Checkbox } from '@mui/material';
import styled from 'styled-components';
import COLORS from '../../colors';
import { SummaryContainer } from '../containers';
import { useAppSelector } from '../../redux/hooks';
import { RootState } from '../../redux/store';
import { useEffect, useState } from 'react';
import { getMonthsInRange } from '../../services/date';
import { mapPascalMonthToLowercaseMonth } from '../../util';
import { MONTH_PASCAL } from '../../constants';
import { getMinimumPeak } from '../../services/calculate';

interface StyledItemProps {
  isHighlighted?: boolean;
}

const StyledColumns = styled.div`
  display: flex;
  flex-direction: row;
  gap: 16px;
  width: 100%;
  margin-bottom: 16px;
  margin-top: 16px;
`;

const Column = styled.div`
  flex: 1;
  width: 100%;
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
`;

const StyledTableHeading = styled.p`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  font-size: 13.33px;
  font-style: normal;
  font-weight: 400;
  line-height: 16px;
  color: ${COLORS.NEUTRAL_500};
  padding: 0 12px;
`;

const StyledTableHeadingSmall = styled.p`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  font-size: 13.33px;
  font-style: normal;
  font-weight: 400;
  line-height: 16px;
  color: ${COLORS.NEUTRAL_500};
  padding: 0 12px;
  max-width: 60px;
`;

const StyledItem = styled.div<StyledItemProps>`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px;
  color: ${COLORS.NEUTRAL_200};
  padding: 12px;
  background-color: ${(props) =>
    props.isHighlighted ? COLORS.NEUTRAL_925 : 'transparent'};
  border-radius: 8px;
`;

const StyledCheckbox = styled(Checkbox)`
  color: ${COLORS.NEUTRAL_400} !important;
  padding: 0 !important;
`;

const PeakPercentage = () => {
  const { demographic } = useAppSelector((state: RootState) => state.detail);
  const { peakLoading } = useAppSelector(
    (state: RootState) => state.spreadsheet
  );
  const monthData = useAppSelector((state: RootState) => state.calculated);

  const { startDate, endDate } = useAppSelector(
    (state: RootState) => state.detail.campaignLength
  );

  const [months, setMonths] = useState<string[]>([]);

  useEffect(() => {
    if (!startDate || !endDate) {
      return;
    }

    const monthsInRange = getMonthsInRange(
      new Date(startDate),
      new Date(endDate)
    );
    setMonths(monthsInRange);
  }, [startDate, endDate]);

  return (
    <SummaryContainer title="Peak %">
      <StyledColumns>
        <Column>
          <Row>
            <StyledTableHeadingSmall></StyledTableHeadingSmall>
            <StyledTableHeading>Minimum Peak</StyledTableHeading>
            <StyledTableHeading>Use Minimum</StyledTableHeading>
            <StyledTableHeading>Final Peak</StyledTableHeading>
          </Row>
          {months.map((month) => {
            if (!month || !demographic) {
              return;
            }

            const selectedMonth = mapPascalMonthToLowercaseMonth(
              month as MONTH_PASCAL
            );

            return (
              <Row>
                <StyledTableHeadingSmall>{month}</StyledTableHeadingSmall>
                <StyledItem>
                  {getMinimumPeak(peakLoading, demographic) * 100}%
                </StyledItem>
                <StyledItem>
                  <StyledCheckbox
                    checked={
                      getMinimumPeak(peakLoading, demographic) * 100 ===
                      monthData[selectedMonth].peak
                    }
                    disabled
                  />
                </StyledItem>
                <StyledItem isHighlighted>
                  {monthData[selectedMonth].peak}%
                </StyledItem>
              </Row>
            );
          })}
        </Column>
      </StyledColumns>
    </SummaryContainer>
  );
};

export default PeakPercentage;
